import React, { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, KanbanSquare, Trash2, Edit3, Archive, ArrowLeft, Download, Upload } from 'lucide-react';
import {
  useListLeadsQuery,
  useDeleteLeadMutation,
  useUpdateLeadStatusMutation,
  useClassifyLeadMutation,
  useConvertLeadMutation,
  useAddLeadFollowUpMutation,
} from '../../api/leadsApi';
import FollowUpDateModal from './FollowUpDateModal';
import { useInfiniteQuery } from '../../hooks/useInfiniteQuery';
import { useUpdateLeadDepartmentMutation } from '../../api/leadsApi';
import {
  LEAD_STATUS, LEAD_PRIORITY, LEAD_CORE_STATUSES, LEAD_DEPARTMENTS,
  LEAD_ACTIONS_NEW, LEAD_ACTIONS_FOLLOWUP,
} from '../../utils/status';
import AddLeadModal from './AddLeadModal';
import ImportLeadsModal from './ImportLeadsModal';
import LeadDetailDrawer from './LeadDetailDrawer';
import ScheduleConsultationModal from '../consultations/ScheduleConsultationModal';
import ConfirmDialog from '../../components/ConfirmDialog';
import { apiErrorMessage } from '../../utils/apiError';
import { toast } from 'react-toastify';

// Pipeline: New → Not Connected / Follow-up / Move to Consultation / Junk.
const STATUS_OPTIONS = LEAD_CORE_STATUSES;

// Which status actions a row offers depends on where the lead currently is in the pipeline:
//  · New (untouched)      → Not Connected / Follow-up / Move to Consultation / Junk
//  · Follow-up            → Not Connected / Move to Consultation / Junk
//  · anything else        → the full core set
const actionsForLead = (status) => {
  if (status === 'New') return LEAD_ACTIONS_NEW;
  if (status === 'FollowUpPending') return LEAD_ACTIONS_FOLLOWUP;
  return LEAD_CORE_STATUSES;
};

// The three list views: untouched New leads · current Follow-ups · everything active.
const VIEWS = [
  { key: 'new', label: 'New Leads', status: 'New' },
  { key: 'followup', label: 'Follow-up', status: 'FollowUpPending' },
  { key: 'all', label: 'All Leads', status: '' },
];

export default function LeadsPage() {
  const [viewJunk, setViewJunk] = useState(false);
  const [view, setView] = useState('new');
  const [filter, setFilter] = useState({ search: '', status: '', department: '' });
  const [updateDepartment] = useUpdateLeadDepartmentMutation();
  const [showAdd, setShowAdd] = useState(false);
  const [openLeadId, setOpenLeadId] = useState(null);
  const [confirmConvert, setConfirmConvert] = useState(null);
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [followUpTarget, setFollowUpTarget] = useState(null); // lead being moved to Follow-up
  const [showImport, setShowImport] = useState(false);
  // Support deep links of the form /leads?openId=<guid> — e.g. clicking a lead from global search.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const id = searchParams.get('openId');
    if (id) setOpenLeadId(id);
    // Deep link /leads?status=NotConnected (top-bar badge) — open the All view pre-filtered.
    const st = searchParams.get('status');
    if (st) {
      setView('all');
      setFilter((f) => ({ ...f, status: st }));
    }
  }, [searchParams]);
  const closeLeadDrawer = () => {
    setOpenLeadId(null);
    if (searchParams.get('openId')) {
      const next = new URLSearchParams(searchParams);
      next.delete('openId');
      setSearchParams(next, { replace: true });
    }
  };

  // The active view (New / Follow-up) pins the status filter; the All view uses the dropdown.
  const viewStatus = VIEWS.find((v) => v.key === view)?.status ?? '';
  const hookFilter = useMemo(
    () => ({ ...filter, status: view === 'all' ? filter.status : viewStatus, junk: viewJunk }),
    [filter, viewJunk, view, viewStatus],
  );
  const { items, total, hasMore, isFetching, sentinelRef, patchItem, removeItem } =
    useInfiniteQuery(useListLeadsQuery, hookFilter, { pageSize: 30 });

  const [deleteLead] = useDeleteLeadMutation();
  const [updateStatus, { isLoading: statusSaving }] = useUpdateLeadStatusMutation();
  const [classify] = useClassifyLeadMutation();
  const [convert] = useConvertLeadMutation();
  const [addFollowUp] = useAddLeadFollowUpMutation();

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead permanently? This cannot be undone.')) return;
    try { await deleteLead(id).unwrap(); removeItem(id); toast.success('Lead deleted'); }
    catch (e) { toast.error(apiErrorMessage(e, 'Failed to delete')); }
  };

  const performConvert = async () => {
    if (!confirmConvert) return;
    const lead = confirmConvert;
    setConfirmConvert(null);
    try {
      const patient = await convert({ id: lead.id, gender: 'Unspecified' }).unwrap();
      removeItem(lead.id); // converted leads leave the active pipeline immediately
      toast.success(`Lead converted → patient ${patient.patientCode} created`);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to convert lead'));
    }
  };

  // Export the currently-loaded lead view to CSV, including the ad-campaign (UTM) columns for marketing.
  const exportCsv = () => {
    const rows = items ?? [];
    if (!rows.length) { toast.info('Nothing to export'); return; }
    const cols = [
      ['Name', 'fullName'], ['Phone', 'phoneNumber'], ['Email', 'email'], ['City', 'city'],
      ['Status', 'status'], ['Priority', 'priority'], ['Source', 'source'],
      ['UTM Source', 'utmSource'], ['Medium/Placement', 'utmMedium'], ['Campaign', 'utmCampaign'],
      ['Ad Set', 'adSetName'], ['Ad', 'adName'], ['Created', 'createdAt'],
    ];
    const esc = (v) => { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const csv = [cols.map((c) => c[0]).join(','), ...rows.map((r) => cols.map((c) => esc(r[c[1]])).join(','))].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const handleStatusChange = async (lead, nextStatus) => {
    if (nextStatus === lead.status) return;

    // Follow-up path — prompt for the callback date first; the status flips on confirm and the
    // lead surfaces in Today's Tasks on the chosen date.
    if (nextStatus === 'FollowUpPending') {
      setFollowUpTarget(lead);
      return;
    }

    // Consultation path — open scheduling modal; backend sets status when consultation is created
    if (nextStatus === 'ConsultationScheduled') {
      setScheduleTarget(lead);
      return;
    }

    // Convert path — needs confirmation since it creates a real patient
    if (nextStatus === 'ConvertedPatient') {
      if (lead.convertedPatientId) {
        toast.info('This lead is already converted.');
        return;
      }
      setConfirmConvert(lead);
      return;
    }

    // Junk path — prompt for a reason and classify (also removes from main view)
    if (nextStatus === 'JunkLead') {
      const reason = window.prompt('Why is this a junk lead? (e.g., wrong number, spam, outside area)');
      if (reason === null) return; // cancelled
      try {
        await classify({ id: lead.id, classification: 'Junk', reason }).unwrap();
        // Junked leads move to the Junk view — drop the row from the active list right away.
        if (!viewJunk) removeItem(lead.id); else patchItem(lead.id, { status: 'JunkLead', classification: 'Junk' });
        toast.success('Lead marked as Junk');
      } catch (e) {
        toast.error(apiErrorMessage(e, 'Failed to mark as Junk'));
      }
      return;
    }

    // Plain status update
    try {
      const updated = await updateStatus({ id: lead.id, status: nextStatus }).unwrap();
      // Reflect the change on the visible row immediately — no reload needed. If the active
      // view/filter pins a status and the row no longer matches it, drop it from the view.
      const pinnedStatus = view === 'all' ? filter.status : viewStatus;
      if (pinnedStatus && nextStatus !== pinnedStatus) removeItem(lead.id);
      else patchItem(lead.id, updated ?? { status: nextStatus });
      toast.success(`Status → ${LEAD_STATUS[nextStatus]?.label ?? nextStatus}`);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to update status'));
    }
  };

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb">
          <span>Home</span><span className="bc-sep">/</span>
          <span className="bc-cur">{viewJunk ? 'Junk Leads' : 'Leads'}</span>
        </div>
        <div className="page-header-row">
          <div>
            <h1>{viewJunk ? 'Junk Leads' : 'Leads'}</h1>
            <div className="page-header-sub">
              {viewJunk
                ? 'Archived junk inquiries. Kept for marketing analytics.'
                : 'Lead inbox, qualification and conversion pipeline.'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {viewJunk ? (
              <button className="btn btn-outline" onClick={() => setViewJunk(false)}>
                <ArrowLeft size={16} /> Back to Leads
              </button>
            ) : (
              <>
                <button
                  className="btn btn-outline"
                  onClick={() => setViewJunk(true)}
                  title="View archived junk leads"
                >
                  <Archive size={16} /> Junk Leads
                </button>
                <Link to="/leads/kanban" className="btn btn-outline"><KanbanSquare size={16} /> Kanban</Link>
                <button className="btn btn-outline" onClick={exportCsv} title="Export current view to CSV (includes campaign/UTM columns)"><Download size={16} /> Export</button>
                <button className="btn btn-outline" onClick={() => setShowImport(true)} title="Bulk-import leads from a CSV or Excel sheet"><Upload size={16} /> Import CSV/Excel</button>
                <button className="btn btn-primary" onClick={() => setShowAdd(true)}><Plus size={16} /> Add Lead</button>
              </>
            )}
          </div>
        </div>
      </div>

      {!viewJunk && (
        <div className="tabs" style={{ marginBottom: 10 }}>
          {VIEWS.map((v) => (
            <button
              key={v.key}
              className={`tab-btn ${view === v.key ? 'active' : ''}`}
              onClick={() => setView(v.key)}
              title={v.key === 'new' ? 'Untouched leads with no status yet'
                : v.key === 'followup' ? 'Every lead currently in Follow-up'
                  : 'All active (non-junk) leads'}
            >
              {v.label}
            </button>
          ))}
        </div>
      )}

      <div className="toolbar">
        <div className="search-field">
          <input className="form-control" placeholder="Search name, phone, email…"
            value={filter.search} onChange={(e) => setFilter({ ...filter, search: e.target.value })} />
        </div>
        {!viewJunk && view === 'all' && (
          <select className="form-control" style={{ width: 200 }} value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}>
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((k) => <option key={k} value={k}>{LEAD_STATUS[k]?.label ?? k}</option>)}
          </select>
        )}
        <select className="form-control" style={{ width: 190 }} value={filter.department}
          onChange={(e) => setFilter({ ...filter, department: e.target.value })}
          title="Filter by clinic department">
          <option value="">All departments</option>
          {LEAD_DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      <div className="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th>Name</th><th>Phone</th><th>Concern</th><th>Source</th><th>Status</th><th>Created</th><th></th>
            </tr>
          </thead>
          <tbody>
            {(items).map((l) => {
              const status = LEAD_STATUS[l.status] || { label: l.status, color: 'b-gray' };
              const priority = LEAD_PRIORITY[l.priority] || { label: l.priority, color: 'b-gray' };
              return (
                <tr key={l.id} style={{ cursor: 'pointer' }} onClick={() => setOpenLeadId(l.id)}>
                  <td className="td-bold">{l.fullName}<div className="td-muted">{l.email || '—'}</div></td>
                  <td className="td-mono">{l.phoneNumber}</td>
                  <td className="td-muted" style={{ maxWidth: 220 }} title={l.notes || ''}>
                    <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {l.notes || '—'}
                    </span>
                  </td>
                  <td>{l.source}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <select
                      className="form-control"
                      style={{ width: 'auto', minWidth: 130, height: 28, padding: '0 22px 0 8px', fontSize: '0.78rem' }}
                      value={l.department || ''}
                      onChange={async (e) => {
                        try { await updateDepartment({ id: l.id, department: e.target.value || null }).unwrap(); }
                        catch (err) { toast.error(apiErrorMessage(err, 'Could not update department')); }
                      }}
                      title="Categorize department"
                    >
                      <option value="">—</option>
                      {LEAD_DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <StatusPill
                      status={l.status}
                      options={actionsForLead(l.status)}
                      onChange={(next) => handleStatusChange(l, next)}
                    />
                  </td>
                  <td><span className={`badge ${priority.color}`}>{priority.label}</span></td>
                  <td className="td-muted">{new Date(l.createdAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</td>
                  <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                    <button className="btn-icon-sm" title="Open" onClick={() => setOpenLeadId(l.id)}><Edit3 size={14} /></button>
                    <button className="btn-icon-sm" title="Delete" onClick={() => handleDelete(l.id)}><Trash2 size={14} color="var(--red-500)" /></button>
                  </td>
                </tr>
              );
            })}
            {isFetching && (
              <tr><td colSpan={9} style={{ textAlign: 'center', padding: 16 }}>
                <div className="skeleton" style={{ height: 18, maxWidth: 240, margin: '0 auto' }} />
              </td></tr>
            )}
            {!isFetching && items.length === 0 && (
              <tr><td colSpan={9}>
                <div className="empty-state">
                  <h3>{viewJunk ? 'No junk leads' : 'No leads yet'}</h3>
                  <p>{viewJunk ? 'Leads marked as junk will appear here.' : 'Click "Add Lead" to capture your first inquiry.'}</p>
                </div>
              </td></tr>
            )}
          </tbody>
        </table>
        {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
      </div>

      {items.length > 0 && (
        <div className="toolbar" style={{ marginTop: 10, justifyContent: 'center' }}>
          <span className="td-muted" style={{ fontSize: '0.8rem' }}>
            Showing {items.length.toLocaleString()} of {total.toLocaleString()}{hasMore ? ' — scroll for more' : ''}
          </span>
        </div>
      )}

      {followUpTarget && (
        <FollowUpDateModal
          leadName={followUpTarget.fullName}
          busy={statusSaving}
          onClose={() => setFollowUpTarget(null)}
          onConfirm={async (whenIso) => {
            const lead = followUpTarget;
            try {
              const updated = await updateStatus({ id: lead.id, status: 'FollowUpPending' }).unwrap();
              await addFollowUp({ id: lead.id, scheduledAt: whenIso, reason: 'Follow-up scheduled' }).unwrap();
              const pinnedStatus = view === 'all' ? filter.status : viewStatus;
              if (pinnedStatus && pinnedStatus !== 'FollowUpPending') removeItem(lead.id);
              else patchItem(lead.id, updated ?? { status: 'FollowUpPending' });
              toast.success(`Follow-up on ${new Date(whenIso).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })} — will appear in Today's Tasks`);
            } catch (e) {
              toast.error(apiErrorMessage(e, 'Failed to schedule the follow-up'));
            } finally {
              setFollowUpTarget(null);
            }
          }}
        />
      )}

      {showAdd && <AddLeadModal onClose={() => setShowAdd(false)} />}
      {showImport && <ImportLeadsModal onClose={() => setShowImport(false)} />}
      {openLeadId && <LeadDetailDrawer leadId={openLeadId} onClose={closeLeadDrawer} />}
      {scheduleTarget && (
        <ScheduleConsultationModal
          leadId={scheduleTarget.id}
          subjectName={scheduleTarget.fullName}
          onClose={() => setScheduleTarget(null)}
          onCreated={() => {
            // Lead moved into the consultation pipeline — it leaves the active list immediately.
            removeItem(scheduleTarget.id);
            setScheduleTarget(null);
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(confirmConvert)}
        title="Convert lead to patient?"
        message={`This will create a new patient record for ${confirmConvert?.fullName ?? 'this lead'} and remove them from the active lead pipeline. The lead history is preserved.`}
        confirmLabel="Convert & Create Patient"
        variant="success"
        onConfirm={performConvert}
        onCancel={() => setConfirmConvert(null)}
      />
    </>
  );
}

/**
 * Compact inline status dropdown rendered to look like a badge.
 * Selecting an option fires onChange(nextStatusKey). The lead's CURRENT status is always
 * rendered (as a disabled option when it isn't itself an offered action) so a "New" lead
 * shows "New" instead of silently displaying the first available action.
 */
function StatusPill({ status, options = STATUS_OPTIONS, onChange }) {
  const meta = LEAD_STATUS[status] || { label: status, color: 'b-gray' };
  return (
    <span className={`badge ${meta.color}`} style={{ padding: 0, paddingLeft: 4 }}>
      <select
        value={status}
        onChange={(e) => onChange(e.target.value)}
        style={{
          background: 'transparent',
          border: 'none',
          fontWeight: 700,
          fontSize: '0.7rem',
          letterSpacing: '0.02em',
          color: 'inherit',
          padding: '3px 18px 3px 4px',
          cursor: 'pointer',
          appearance: 'none',
          backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'8\' height=\'5\' viewBox=\'0 0 8 5\'><path d=\'M0 0l4 5 4-5z\' fill=\'currentColor\'/></svg>")',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 6px center',
        }}
        title="Change status"
      >
        {!options.includes(status) && (
          <option value={status} disabled style={{ color: 'var(--text-muted)', background: 'var(--bg-card)' }}>
            {meta.label}
          </option>
        )}
        {options.map((k) => (
          <option key={k} value={k} style={{ color: 'var(--text-primary)', background: 'var(--bg-card)' }}>
            {LEAD_STATUS[k]?.label ?? k}
          </option>
        ))}
      </select>
    </span>
  );
}
